import { useMutation } from "@tanstack/react-query";
import { BlueBox } from "components/Atoms/BlueBox";
import { SearchInput } from "components/Atoms/SearchInput";
import { SuggestionsMenu, SuggestionsMenuOption } from "components/Atoms/SuggestionsMenu";
import React from "react";
import SupportArticleLink from "supportbeacon/SupportArticleLink";
import { useCustomQuery } from "utilities/hookutils";
import { useDebounce } from "utilities/usehooks";
import { v4 as uuidv4 } from "uuid";
import { GooglePlaceState } from "shared/utils/fieldsHelper/groundFields";

interface Props {
  onPlaceSelected: (place: GooglePlaceState) => void;
  calledFrom: "train" | "ground";
  focusOnMount?: boolean;
  hasError?: boolean;
  placeType?: server.dto.GooglePlaceTypes;
}

export const GooglePlaceInput = (props: Props) => {
  const [sessionToken, setUuid] = React.useState<string>("");

  React.useEffect(() => {
    // Generate a UUID for the session token
    setUuid(uuidv4());
  }, []);

  // Results are hidden until the first API response comes back
  const [displayResults, setDisplayResults] = React.useState(false);

  // The first API call is made after the debounce time has passed
  const [isSearchEnabled, setIsSearchEnabled] = React.useState(false);

  // We only start making API calls after the user starts typing
  // So not if the `name` prop is changed by the parent component
  const [isTyping, setIsTyping] = React.useState(false);

  const [query, setQuery] = React.useState("");
  const debounceQuery = useDebounce(query, 500);
  const isDebouncing = debounceQuery !== query;

  const onInputChange = (newQuery: string) => {
    setQuery(newQuery);
    setIsTyping(true);
  }

  const getGooglePlaceDetails = useMutation((placeIdentifier: string) =>
    window.CoreApi.GooglePlace.GetPlace({ placeIdentifier, sessionToken, calledFrom: props.calledFrom }).then(r => r.data.data));

  const onItemSelected = async (place: server.dto.GooglePlaceAutocomplete) => {
    if (getGooglePlaceDetails.isLoading) return;

    setIsSearchEnabled(false);
    setDisplayResults(false);
    setIsTyping(false);
    const res = await getGooglePlaceDetails.mutateAsync(place.placeIdentifier);
    props.onPlaceSelected({
      googlePlaceId: res.googlePlaceId,
      placeIdentifier: res.placeIdentifier,
      name: res.name,
      address: [res.address.addressLine1, res.address.addressLine2, res.address.city, res.address.country].filter(Boolean).join(", "),
      geo: res.latLng,
    });
  }

  // If the user starts typing, we enable the Google Places search after the debounce time has passed
  React.useEffect(() => {
    if (!isDebouncing && isTyping && !isSearchEnabled) {
      setIsSearchEnabled(true);
    }
  }, [debounceQuery, isTyping, isSearchEnabled])

  const queryKey = ["googlePlacesAutoComplete", debounceQuery];

  const api = useCustomQuery({
    queryKey,
    queryFn: () => window.CoreApi.GooglePlace.Autocomplete({
      placeType: props.placeType,
      query: debounceQuery,
      sessionToken: sessionToken,
    }).then(r => r.data.data),
    keepPreviousData: true,
    enabled: isSearchEnabled && isTyping && debounceQuery.length > 2,
    onSuccess: () => {
      setDisplayResults(true);
    }
  });

  const onOpen = () => {
    if (isSearchEnabled && api.isSuccess) setDisplayResults(true);
  }

  return <SuggestionsMenu
    isOpen={displayResults && query !== ""}
    onOpen={onOpen}
    onClose={() => setDisplayResults(false)}
    renderMenu={() => <>
      {api.data.length === 0 && <div className="padding-small">
        <BlueBox>
          <>No match found on <SupportArticleLink articleId={serverReference.supportArticleIds.googlePlacesIntegration}>Google Places</SupportArticleLink></>
        </BlueBox>
      </div>}

      {api.data.length > 0 && <>
        {api.data.map(item => <SuggestionsMenuOption
          key={item.placeIdentifier}
          onClick={() => onItemSelected(item)}
        >
          <div>{item.mainText}</div>
          <div className="s1_gray s1_small">{item.secondaryText}</div>
        </SuggestionsMenuOption>)}
        <div className="padding-small">
          <BlueBox>
            <>Results provided by <SupportArticleLink articleId={serverReference.supportArticleIds.googlePlacesIntegration}>Google Places</SupportArticleLink></>
          </BlueBox>
        </div>
      </>}
    </>}
  >
    <SearchInput
      hasError={props.hasError}
      focusOnMount={props.focusOnMount}
      value={query}
      onChange={value => onInputChange(value || "")}
      placeholder={RESX.GeneralLabels.Search}
      isLoading={api.isFetching || getGooglePlaceDetails.isLoading}
    />
  </SuggestionsMenu>
}